/**
 * Shared tool definitions consumed by both Studio's app-switch-bar and
 * the homepage main navigation (cf-menu). This file is pure data — no
 * React, no hooks, no JSX, no translations.
 */

import {
  TOOL_FEATURE_FLAG_VIDEO_UNCENSORED,
  TOOL_FEATURE_FLAG_YOUTUBE_THUMBNAIL_GENERATOR,
} from './tool-feature-flags'

export const TOOL_CLUSTERS = [
  'image',
  'video',
  'audio',
  '3d',
  'marketing',
  'workspaces',
  'utilities',
  'entertainment',
  'crafting',
] as const

export type ToolCluster = (typeof TOOL_CLUSTERS)[number]

export const CLUSTER_ROUTES: Record<ToolCluster, string> = {
  image: '/image',
  video: '/video',
  audio: '/audio',
  '3d': '/3d-tools',
  marketing: '/marketing',
  workspaces: '/workspaces',
  utilities: '/utilities',
  entertainment: '/entertainment',
  crafting: '/crafting',
}

export interface ToolDefinition {
  /** Translation key matching the ToolTypeTranslationKey pattern, e.g. 'TOOL_TYPE_IMAGE_EDITOR' */
  translationKey: string
  /** Which cluster this tool belongs to */
  cluster: ToolCluster
  /** Route path relative to studio base URL, e.g. '/ai-image-editor' */
  route: string
  /** Display order within the cluster */
  order: number
  /** GrowthBook feature flag name — tool is hidden when flag is off */
  featureFlag?: string
}

export const TOOL_DEFINITIONS: ToolDefinition[] = [
  // ── image ──
  {
    translationKey: 'TOOL_TYPE_IMAGE_EDITOR',
    cluster: 'image',
    route: '/ai-image-editor',
    order: 0,
  },
  { translationKey: 'TOOL_TYPE_FLOW', cluster: 'image', route: '/flow', order: 1 },
  {
    // Navigation-only tool (no protobuf ToolType) — also injected into the Studio
    // sidebar via useNavigationOnlyItems(); listed here so the homepage menu shows it too.
    translationKey: 'TOOL_TYPE_IMAGE_OUTPAINTING',
    cluster: 'image',
    route: '/image-outpainting',
    order: 1.05,
  },
  {
    // Navigation-only tool (no protobuf ToolType) — also injected into the Studio
    // sidebar via useNavigationOnlyItems(); listed here so the homepage menu shows it too.
    translationKey: 'TOOL_TYPE_IMAGE_INPAINTING',
    cluster: 'image',
    route: '/image-inpainting',
    order: 1.06,
  },
  {
    // Navigation-only tool (no protobuf ToolType) — also injected into the Studio
    // sidebar via useNavigationOnlyItems(); listed here so the homepage menu shows it too.
    translationKey: 'TOOL_TYPE_CHARACTER_SHEET',
    cluster: 'image',
    route: '/character-sheet',
    order: 1.1,
  },
  {
    translationKey: 'TOOL_TYPE_AI_PHOTO_STYLES',
    cluster: 'image',
    route: '/ai-photo-styles',
    order: 1.5,
  },
  {
    translationKey: 'TOOL_TYPE_AI_MOCKUP_GENERATOR',
    cluster: 'image',
    route: '/ai-mockup-generator',
    order: 1.6,
  },
  { translationKey: 'TOOL_TYPE_VECTORIZER', cluster: 'image', route: '/vectorizer', order: 2 },
  {
    translationKey: 'TOOL_TYPE_AISVG_GENERATOR',
    cluster: 'image',
    route: '/ai-svg-generator',
    order: 2.5,
  },
  {
    translationKey: 'TOOL_TYPE_UPSCALER',
    cluster: 'image',
    route: '/image-upscaler',
    order: 3,
  },
  {
    translationKey: 'TOOL_TYPE_BG_REMOVER',
    cluster: 'image',
    route: '/background-remover',
    order: 4,
  },
  {
    translationKey: 'TOOL_TYPE_PEOPLE_REMOVER_FROM_BACKGROUND',
    cluster: 'image',
    route: '/remove-people-from-background',
    order: 5,
  },
  {
    translationKey: 'TOOL_TYPE_LOGO_GENERATOR',
    cluster: 'image',
    route: '/logo-creator',
    order: 6,
  },
  { translationKey: 'TOOL_TYPE_CAMERA_ANGLE', cluster: 'image', route: '/camera-angle', order: 25 },
  {
    translationKey: 'TOOL_TYPE_IMAGE_LAYERIZER',
    cluster: 'image',
    route: '/image-layerizer',
    order: 26,
  },

  // ── video ──
  {
    translationKey: 'TOOL_TYPE_VIDEO_GENERATOR',
    cluster: 'video',
    route: '/ai-video-generator',
    order: 9,
  },
  {
    translationKey: 'TOOL_TYPE_VIDEO_DRAFT',
    cluster: 'video',
    route: '/draft-mode',
    order: 9.05,
  },
  {
    translationKey: 'TOOL_TYPE_VIDEO_UNCENSORED',
    cluster: 'video',
    route: '/ai-video-uncensored',
    order: 9.1,
    featureFlag: TOOL_FEATURE_FLAG_VIDEO_UNCENSORED,
  },
  {
    translationKey: 'TOOL_TYPE_VIDEO_EDITOR',
    cluster: 'video',
    route: '/ai-video-editor',
    order: 9.5,
  },
  {
    translationKey: 'TOOL_TYPE_VIDEO_UPSCALER',
    cluster: 'video',
    route: '/ai-video-upscaler',
    order: 10,
  },
  {
    translationKey: 'TOOL_TYPE_IMAGE_ALIVE',
    cluster: 'video',
    route: '/image-alive',
    order: 11,
  },
  {
    translationKey: 'TOOL_TYPE_VIDEO_EDITING_TOOLBOX',
    cluster: 'video',
    route: '/video-editing-toolbox',
    order: 12,
  },
  {
    translationKey: 'TOOL_TYPE_MARKETING_TEMPLATES',
    cluster: 'marketing',
    route: '/marketing-templates',
    order: 1,
  },
  {
    translationKey: 'TOOL_TYPE_AD_CAMPAIGN_GENERATOR',
    cluster: 'marketing',
    route: '/ad-campaign-generator',
    order: 2,
  },
  {
    translationKey: 'TOOL_TYPE_SUBTITLE_GENERATOR',
    cluster: 'marketing',
    route: '/subtitle-generator',
    order: 3,
  },
  {
    translationKey: 'TOOL_TYPE_PRODUCT_MANAGER',
    cluster: 'marketing',
    route: '/product-manager',
    order: 4,
  },
  {
    translationKey: 'TOOL_TYPE_YOUTUBE_THUMBNAIL_GENERATOR',
    cluster: 'marketing',
    route: '/youtube-thumbnail-generator',
    order: 5,
    featureFlag: TOOL_FEATURE_FLAG_YOUTUBE_THUMBNAIL_GENERATOR,
  },

  // ── audio ──
  {
    translationKey: 'TOOL_TYPE_AI_MUSIC_GENERATOR',
    cluster: 'audio',
    route: '/ai-music-generator',
    order: 9,
  },
  {
    translationKey: 'TOOL_TYPE_SOUND_GENERATOR',
    cluster: 'audio',
    route: '/ai-sound-effect-generator',
    order: 10,
  },
  {
    translationKey: 'TOOL_TYPE_SPEECH_GENERATOR',
    cluster: 'audio',
    route: '/ai-speech-generator',
    order: 11,
  },

  // ── 3d ──
  {
    translationKey: 'TOOL_TYPE_THREED_MODEL_VIEWER',
    cluster: '3d',
    route: '/3d-model-viewer',
    order: 16,
  },
  {
    translationKey: 'TOOL_TYPE_THREED_PRINT_GENERATOR',
    cluster: '3d',
    route: '/3d-print-generator',
    order: 18,
  },
  {
    translationKey: 'TOOL_TYPE_MESH_GENERATOR',
    cluster: '3d',
    route: '/3d-model-generator',
    order: 19,
  },
  { translationKey: 'TOOL_TYPE_THREED_SCULPT', cluster: '3d', route: '/3d-sculpt', order: 24 },
  {
    translationKey: 'TOOL_TYPE_SPLAT_GENERATOR',
    cluster: '3d',
    route: '/world-creator',
    order: 25,
  },

  // ── workspaces ──
  { translationKey: 'TOOL_TYPE_LUMO', cluster: 'workspaces', route: '/lumo', order: 15 },
  {
    // Navigation-only tool (no protobuf ToolType) — also injected into the Studio
    // sidebar via useNavigationOnlyItems(); listed here so the homepage menu shows it too.
    translationKey: 'TOOL_TYPE_WORKFLOWS',
    cluster: 'workspaces',
    route: '/workflows',
    order: 15.5,
  },
  {
    translationKey: 'TOOL_TYPE_STUDIO_DESIGNER',
    cluster: 'workspaces',
    route: '/studio/create',
    order: 16,
  },

  // ── utilities ──
  {
    translationKey: 'TOOL_TYPE_DESIGN_REVIEWER',
    cluster: 'utilities',
    route: '/ai-design-reviewer',
    order: 8,
  },
  {
    translationKey: 'TOOL_TYPE_AI_WATERMARK_REMOVER',
    cluster: 'utilities',
    route: '/ai-watermark-remover',
    order: 12,
  },
  {
    translationKey: 'TOOL_TYPE_FILE_CONVERTER',
    cluster: 'utilities',
    route: '/file-converter',
    order: 12,
  },
  {
    translationKey: 'TOOL_TYPE_IMAGE_COMPRESSOR',
    cluster: 'utilities',
    route: '/image-compressor',
    order: 13,
  },
  { translationKey: 'TOOL_TYPE_TRANSFERS', cluster: 'utilities', route: '/transfer', order: 14 },
  {
    translationKey: 'TOOL_TYPE_IMAGE_RESIZER',
    cluster: 'utilities',
    route: '/image-resizer',
    order: 17,
  },
  {
    translationKey: 'TOOL_TYPE_COLOR_PALETTE_PICKER',
    cluster: 'utilities',
    route: '/color-palette-picker',
    order: 19,
  },
  { translationKey: 'TOOL_TYPE_ETSY_COACH', cluster: 'utilities', route: '/etsy-coach', order: 22 },
  {
    translationKey: 'TOOL_TYPE_COLOR_CONVERTER',
    cluster: 'utilities',
    route: '/color-converter',
    order: 100,
  },
  {
    translationKey: 'TOOL_TYPE_CONTRAST_CHECKER',
    cluster: 'utilities',
    route: '/contrast-checker',
    order: 101,
  },

  // ── entertainment ──
  {
    translationKey: 'TOOL_TYPE_MOMENTS',
    cluster: 'entertainment',
    route: '/moments',
    order: 24,
  },
  {
    translationKey: 'TOOL_TYPE_DOODLE_PET_GENERATOR',
    cluster: 'entertainment',
    route: '/doodle-pet-generator',
    order: 18,
  },
  {
    translationKey: 'TOOL_TYPE_AI_COLORING_PAGE_MAKER',
    cluster: 'entertainment',
    route: '/ai-coloring-page-maker',
    order: 20,
  },
  {
    translationKey: 'TOOL_TYPE_TOON_FAMILY_BUILDER',
    cluster: 'entertainment',
    route: '/toon-family-builder',
    order: 23,
  },

  // ── crafting ──
  {
    translationKey: 'TOOL_TYPE_QUOTE_GENERATOR',
    cluster: 'crafting',
    route: '/ai-quote-generator',
    order: 7,
  },
  {
    translationKey: 'TOOL_TYPE_COLLAGE_MAKER',
    cluster: 'crafting',
    route: '/ai-collage-maker',
    order: 7.5,
  },
  {
    translationKey: 'TOOL_TYPE_WEDDING_INVITE_GENERATOR',
    cluster: 'crafting',
    route: '/ai-wedding-invite-generator',
    order: 21,
  },
]
